import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    input,
    signal,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { PopoverComponent } from "../../modules/s25-popover/popover.component";
import { EventAssignedRoleContact, EventChatStore, EventChatType } from "./event.chat.store";

@TypeManagerDecorator("s25-ng-event-chat-trigger")
@Component({
    selector: "s25-ng-event-chat-trigger",
    standalone: true,
    imports: [S25IconComponent, PopoverComponent],
    host: { class: "chat-dropdown" },
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <s25-popover
            #popover
            [popoverTemplate]="chatMenuTemplate"
            [openTrigger]="'click'"
            [closeTrigger]="'click'"
            [placement]="['bottom-end']"
            [openDelayMillis]="0"
            [closeDelayMillis]="0"
            [popoverClass]="'chat-popover'"
            (shown)="chatMenuOpen.set(true)"
            (hidden)="chatMenuOpen.set(false)"
        >
            <button
                class="chat-trigger"
                [class.active]="chatMenuOpen()"
                [attr.aria-label]="hasUnreadChats() ? 'Chat, unread' : null"
            >
                <s25-ng-icon [type]="'chat'" [ariaLabel]="false"></s25-ng-icon>
                <span class="chat-label">
                    Chat
                    @if (hasUnreadChats()) {
                        <span class="unread-dot" aria-hidden="true"></span>
                    }
                </span>
                <s25-ng-icon [type]="'caretDown'" [ariaLabel]="false" [size]="14"></s25-ng-icon>
            </button>
        </s25-popover>

        <ng-template #chatMenuTemplate>
            <div class="chat-menu">
                <!--                <div class="chat-menu-info">-->
                <!--                    <div class="chat-menu-info-title">Who is in “Everyone”?</div>-->
                <!--                    <p class="chat-menu-info-text">-->
                <!--                        Contacts assigned to <strong>roles on this event</strong>the same people shown in-->
                <!--                        <strong>Event details</strong> next to each contact role (Scheduler, Requestor, Instructor, and-->
                <!--                        any custom roles your site uses).-->
                <!--                    </p>-->
                <!--                    @if (store.eventResource.isLoading()) {-->
                <!--                        <p class="chat-menu-info-muted">Loading contacts…</p>-->
                <!--                    } @else if (!store.everyoneRoleContacts().length) {-->
                <!--                        <p class="chat-menu-info-muted">No contacts are assigned to roles on this event yet.</p>-->
                <!--                    } @else {-->
                <!--                        <ul class="chat-menu-participant-list">-->
                <!--                            @for (assigned of store.everyoneRoleContacts(); track assigned.contactId) {-->
                <!--                                <li>{{ participantSummary(assigned) }}</li>-->
                <!--                            }-->
                <!--                        </ul>-->
                <!--                    }-->
                <!--                </div>-->
                <button class="chat-option" (click)="startChat('all')">Chat with Everyone</button>
                <button class="chat-option" (click)="startChat('scheduler')">Chat with Scheduler</button>
                <button class="chat-option" (click)="startChat('requestor')">Chat with Requestor</button>
                @if (hasInstructor()) {
                    <button class="chat-option" (click)="startChat('instructor')">Chat with Instructor</button>
                }
            </div>
        </ng-template>
    `,
    styles: `
        :host {
            position: relative;
        }

        .chat-trigger {
            position: relative;
            padding: 0 0.75em;
            height: 2em;
            background: none;
            border: none;
            cursor: pointer;
            font-size: inherit;
            color: var(--color-primary);
            transition: all var(--transition-medium);
        }

        .chat-trigger:hover,
        .chat-trigger.active {
            color: var(--color-primary-dark, #1c6a91);
        }

        .chat-label {
            position: relative;
        }

        .unread-dot {
            position: absolute;
            top: -0;
            right: -8px;
            width: 8px;
            height: 8px;
            background-color: red;
            border-radius: 50%;
        }

        .dropdown-arrow {
            font-size: 10px;
            transition: transform var(--transition-medium);
        }

        .chat-trigger.active .dropdown-arrow {
            transform: rotate(180deg);
        }

        .chat-menu {
            background: var(--color--surface-bg);
            border-radius: var(--border-radius);
            max-width: min(280px, calc(100vw - 1rem));
            overflow: hidden;
        }

        ::ng-deep .chat-popover .popover-body {
            padding: 0;
            max-width: min(280px, calc(100vw - 1rem));
        }

        .chat-menu-info {
            padding: 12px 14px 10px;
            border-bottom: 1px solid var(--color--border);
            background: var(--color--page-bg);
        }

        .chat-menu-info-title {
            font-size: 12px;
            font-weight: 600;
            color: var(--color--text-strong);
            margin-bottom: 6px;
        }

        .chat-menu-info-text {
            font-size: 11px;
            line-height: 1.4;
            color: var(--color--text);
            margin: 0 0 8px;
        }

        .chat-menu-info-muted {
            font-size: 11px;
            color: var(--color--text-muted);
            margin: 0;
        }

        .chat-menu-participant-list {
            margin: 0;
            padding-left: 1.15em;
            max-height: 11em;
            overflow-y: auto;
            font-size: 11px;
            line-height: 1.35;
            color: var(--color--text);
        }

        .chat-menu-participant-list li {
            margin-bottom: 2px;
        }

        .chat-option {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            padding: 12px 16px;
            border: none;
            background: none;
            cursor: pointer;
            font-size: 14px;
            color: var(--color--text);
            text-align: left;
            transition: background var(--transition-short);
        }

        .chat-option:hover {
            background: var(--color--surface-2-bg);
        }

        .chat-option:not(:last-child) {
            border-bottom: 1px solid var(--color--border);
        }
    `,
})
export class EventChatTriggerComponent {
    readonly hasUnreadChats = input<boolean>(false);

    protected readonly store = inject(EventChatStore);
    private readonly destroyRef = inject(DestroyRef);
    private readonly popover = viewChild<PopoverComponent>("popover");
    protected readonly chatMenuOpen = signal(false);
    protected readonly hasInstructor = computed(() => this.store.hasInstructor());

    constructor() {
        const closeOnBack = () => this.popover()?.forceClose();
        window.addEventListener("popstate", closeOnBack);
        this.destroyRef.onDestroy(() => window.removeEventListener("popstate", closeOnBack));
    }

    protected startChat(chatType: EventChatType) {
        this.popover()?.forceClose();
        void this.store.startChat(chatType);
    }

    protected participantSummary(row: EventAssignedRoleContact): string {
        const displayName = row.name?.trim() || `Contact ${row.contactId}`;
        return `${displayName} — ${row.roleLabels.join(", ")}`;
    }
}
